import React, { useState } from 'react';
import { Button, HeadingText30 } from '@modernhealth/vital-components';
import moment from 'moment';
import { SessionListItem } from '@modernhealth/circles-web-components';
import {
    CircleTopicRsvpFragment,
    useGetCalendarLinksLazyQuery,
} from '@modernhealth/circles-graphql/v2';
import { useTranslation } from 'react-i18next';

import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';
import { ExternalLinkButton } from 'src/shared/components/ExternalLinkButton';

import { dateAddMinutes } from '../pages/CirclesTopicPage';
import { routes } from '../CirclesRouter';

import { CirclesAddToCalendarModal, useCalendarLinksForCircle } from './CirclesAddToCalendarButton';

interface SessionActionButtonProps {
    meetingURL?: string | null;
    diff: number;
}

export const CircleActiveRsvpList: React.FC<{
    activeRsvps: CircleTopicRsvpFragment[];
    duration: number;
    topicSlug: string;
}> = ({ activeRsvps, duration, topicSlug }) => {
    const [fetchCalendarLinks, calendarLinksQuery] = useGetCalendarLinksLazyQuery({});
    const [showModal, setShowModal] = useState(false);
    const calendarLinks = useCalendarLinksForCircle(calendarLinksQuery.data);
    const { t } = useTranslation('circles');
    return (
        <>
            <HeadingText30 marginTop={48} marginBottom={30}>
                {t('pages.topic.your_session.header', { count: activeRsvps.length })}
            </HeadingText30>
            {activeRsvps.map(rsvp => {
                if (!rsvp) {
                    return null;
                }

                const sessionEndTime = dateAddMinutes(rsvp.session.startTime || '', duration);

                const providersNames = rsvp.session.facilitators.map(
                    provider => provider?.name ?? '',
                );

                const CancelButton = (
                    <InternalLinkButton
                        fluid
                        variant="secondary"
                        size="small"
                        to={routes.topic(topicSlug).registered(rsvp.id).cancel}
                        css={{ textAlign: 'center' }}
                    >
                        {t('session.rsvp.cancel_button.title')}
                    </InternalLinkButton>
                );

                const diff = moment(rsvp.session.startTime).diff(moment(), 'minutes');

                const SessionActionButton = ({ meetingURL, diff }: SessionActionButtonProps) => {
                    return meetingURL && diff < 15 ? (
                        <ExternalLinkButton fluid variant="primary" size="small" href={meetingURL}>
                            {t('session.rsvp.join_button.title')}
                        </ExternalLinkButton>
                    ) : (
                        <Button
                            variant="primary"
                            size="small"
                            fluid
                            disabled={calendarLinksQuery.loading}
                            onClick={() => {
                                fetchCalendarLinks({
                                    variables: {
                                        rsvpId: rsvp.id,
                                    },
                                });

                                setShowModal(true);
                            }}
                        >
                            {t('session.rsvp.add_to_calendar_button.title')}
                        </Button>
                    );
                };

                return (
                    <div key={rsvp.id}>
                        {calendarLinks ? (
                            <CirclesAddToCalendarModal
                                visible={showModal}
                                onClose={() => {
                                    setShowModal(false);
                                }}
                                calendarLinks={calendarLinks}
                            />
                        ) : null}
                        <SessionListItem
                            providersNames={providersNames}
                            startTime={rsvp.session.startTime}
                            endTime={sessionEndTime.toISOString()}
                            bookingStatus={rsvp.session.bookingStatus}
                            primaryButton={
                                <SessionActionButton meetingURL={rsvp.joinSessionUrl} diff={diff} />
                            }
                            secondaryButton={CancelButton}
                        />
                    </div>
                );
            })}
        </>
    );
};
